import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';

const MessageActions = ({ 
  message, 
  onReply, 
  onEdit, 
  onDelete, 
  onPin, 
  onCopyLink,
  canEdit = false,
  canDelete = false,
  canPin = false,
  className = ""
}) => {
  const [showActions, setShowActions] = useState(false);

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast.success('Message copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy message');
    }
    setShowActions(false);
  };

  const handleCopyLink = async () => {
    try {
      await onCopyLink(message.id);
      toast.success('Link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
    setShowActions(false);
  };

  const actions = [
    {
      icon: 'Reply',
      label: 'Reply',
      action: () => {
        onReply(message);
        setShowActions(false);
      },
      show: true,
      color: 'text-blue-400 hover:text-blue-300'
    },
    {
      icon: 'Copy',
      label: 'Copy message',
      action: handleCopyMessage,
      show: true,
      color: 'text-gray-400 hover:text-gray-300'
    },
    {
      icon: 'Link',
      label: 'Copy link',
      action: handleCopyLink,
      show: !!onCopyLink,
      color: 'text-gray-400 hover:text-gray-300'
    },
    {
      icon: 'Edit',
      label: 'Edit',
      action: () => {
        onEdit(message);
        setShowActions(false);
      },
      show: canEdit,
      color: 'text-yellow-400 hover:text-yellow-300'
    },
    {
      icon: 'Pin',
      label: message.pinned ? 'Unpin' : 'Pin',
      action: () => {
        onPin(message);
        setShowActions(false);
      },
      show: canPin,
      color: 'text-green-400 hover:text-green-300'
    },
    {
      icon: 'Trash2',
      label: 'Delete',
      action: () => {
        onDelete(message);
        setShowActions(false);
      },
      show: canDelete,
      color: 'text-red-400 hover:text-red-300'
    }
  ];

  const visibleActions = actions.filter(action => action.show);

  return (
    <div className={`message-actions relative ${className}`}>
      <button
        onClick={() => setShowActions(!showActions)}
        className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 absolute top-1 right-1"
      >
        <Icon name="MoreHorizontal" size={12} />
      </button>

      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -10 }}
            className="absolute top-8 right-0 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg py-1 shadow-xl z-50 min-w-[140px]"
          >
            {visibleActions.map((action, index) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={action.action}
                className={`w-full px-3 py-2 text-left hover:bg-white/10 transition-colors flex items-center space-x-2 text-sm ${action.color}`}
              >
                <Icon name={action.icon} size={14} />
                <span>{action.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Reply Button - positioned to the side */}
      <div className="hidden md:block absolute -left-8 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onReply(message)}
          className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-blue-400 hover:text-blue-300 flex items-center justify-center shadow-lg"
          title="Reply"
        >
          <Icon name="Reply" size={14} />
        </motion.button>
      </div>

      {/* Click outside to close */}
      {showActions && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowActions(false)}
        />
      )}
    </div>
  );
};

export default MessageActions;